import { z } from 'zod';

/**
 * The six PMS noyau codes (the canonical archetype identifiers) and their colour
 * keys. These live in @words_interface/shared because both the backend (bulk
 * parsing / conflict detection) and the frontend (word + bulk schemas) need the
 * same source of truth. The frontend's schemas/word.ts re-exports them.
 */
export const noyauCodeSchema = z.enum(['SGP', 'SPG', 'PSG', 'PGS', 'GPS', 'GSP']);
export type NoyauCode = z.infer<typeof noyauCodeSchema>;

export const noyauKeySchema = z.enum(['bleunuit', 'bleuciel', 'vert', 'jaune', 'rouge', 'rose']);
export type NoyauKey = z.infer<typeof noyauKeySchema>;

/**
 * The five fixed `pms_tag.tag_category` values, pinned as the backend contract
 * (see docs/Design_ Visuel UI_UX/word-detail-rework-plan.md §4). Anything
 * else — including the ~100%-null reality on current devdb — degrades to the
 * "Non catégorisé" bucket in the UI rather than being one of these values.
 */
export const tagCategorySchema = z.enum(['type', 'date', 'geo', 'metier', 'langue']);
export type TagCategory = z.infer<typeof tagCategorySchema>;

/** A word tag: a free-form label, optionally tied to a noyau colour and a `pms_tag` category/frequency. */
export const wordTagSchema = z.object({
  label: z.string(),
  noyauKey: noyauKeySchema.optional(),
  /** `pms_tag.tag_category` — null/absent means "Non catégorisé" (the common case today). */
  tagCategory: tagCategorySchema.nullable().optional(),
  /** `pms_tag.frequency` — usage count, drives picker ordering. Absent for tags with no `pms_tag` row yet. */
  frequency: z.number().optional(),
});
export type WordTag = z.infer<typeof wordTagSchema>;
